import React, { useState, useEffect } from 'react'
import {
  Table, Button, Space, Input, Select, Card, Tag, Tooltip,
  Row, Col, Progress, Popconfirm, message, Modal, Form, Badge,
} from 'antd'
import {
  PlusOutlined, EditOutlined, EyeOutlined, CheckCircleOutlined,
  CloseCircleOutlined, SearchOutlined, ReloadOutlined, LockOutlined,
  ThunderboltOutlined, PieChartOutlined, FileProtectOutlined,
  ExclamationCircleOutlined, ClockCircleOutlined, CheckSquareOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { AllocationStatus } from '@/types'
import { MockAllocation, allocationStore } from './allocationStore'
import { invoiceStore, Invoice } from '@/pages/Invoice/invoiceStore'
import { useMasterData } from '@/context/MasterDataContext'
import { formatPlaceOfSupply, isIntraState } from '@/utils/gstStates'
import { generateCode } from '@/utils/codeGenerator'
import { resolveGeneratorTariff, resolveConsumerTariff } from '@/utils/tariffResolver'
import { ApiError } from '@/services/api'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import './Allocation.css'

// Fallback only — used if the Code Master call itself fails (offline/server
// error), so approval can still complete instead of blocking on invoice
// numbering. Every invoice number normally comes from generateCode('invoices')
// below so it stays in the same INV0001-style sequence as invoices created
// anywhere else in the app.
const invoiceSuffix = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`.toUpperCase()

/* ── Helpers ─────────────────────────────────────────────── */
const formatDate = (value?: string, opts?: Intl.DateTimeFormatOptions) => {
  if (!value) return ''
  const d = new Date(value)
  return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-IN', opts)
}

const STATUS_CFG: Record<AllocationStatus, { color: string; label: string; icon: React.ReactNode }> = {
  [AllocationStatus.DRAFT]:        { color: 'default',   label: 'Draft',        icon: <FileProtectOutlined /> },
  [AllocationStatus.SUBMITTED]:    { color: 'processing', label: 'Submitted',    icon: <ClockCircleOutlined /> },
  [AllocationStatus.UNDER_REVIEW]: { color: 'warning',   label: 'Under Review', icon: <ExclamationCircleOutlined /> },
  [AllocationStatus.APPROVED]:     { color: 'success',   label: 'Approved',     icon: <CheckCircleOutlined /> },
  [AllocationStatus.LOCKED]:       { color: 'cyan',      label: 'Locked',       icon: <LockOutlined /> },
  [AllocationStatus.REJECTED]:     { color: 'error',     label: 'Rejected',     icon: <CloseCircleOutlined /> },
}

/* ── Component ───────────────────────────────────────────── */
const AllocationList: React.FC = () => {
  const navigate = useNavigate()
  const { consumers: masterCons, generators: masterGens, gstConfigs, tariffs, powerTypes } = useMasterData()
  const [allocations, setAllocations] = useState<MockAllocation[]>(() => allocationStore.getAll())
  const [search, setSearch]         = useState('')
  const [filterStatus, setStatus]   = useState<string>('')
  const [filterGen, setFilterGen]   = useState<string>('')
  const [filterPowerType, setFilterPowerType] = useState<string>('')
  const [filterPeriod, setFilterPeriod] = useState<string>('')
  const [pendingOnly, setPendingOnly] = useState(false)
  const [rejectModal, setRejectModal] = useState<{ open: boolean; id: string }>({ open: false, id: '' })
  const [rejectForm] = Form.useForm()
  const [selectedReason, setSelectedReason] = useState<string>('')

  /* Hydrate from the DB on mount and stay live on any add / edit / status change */
  useEffect(() => {
    const update = () => setAllocations(allocationStore.getAll())
    update()
    allocationStore.refresh().then(update)
    window.addEventListener(allocationStore.eventName, update)
    return () => window.removeEventListener(allocationStore.eventName, update)
  }, [])

  // Re-render when invoices change (e.g. after Approve / Generate Invoices),
  // so the "Generate Invoices" button's missing-invoice check stays current.
  const [, setInvoiceTick] = useState(0)
  useEffect(() => {
    const bump = () => setInvoiceTick(t => t + 1)
    if (!invoiceStore.loaded) invoiceStore.refresh().then(bump)
    window.addEventListener(invoiceStore.eventName, bump)
    return () => window.removeEventListener(invoiceStore.eventName, bump)
  }, [])

  const REJECTION_REASONS = [
    'Allocated units exceed approved threshold',
    'T&D loss configuration mismatch',
    'Consumer share percentages do not total 100%',
    'Meter reading discrepancy with TANGEDCO records',
    'Incorrect mapping applied for the period',
    'Manual override not justified with remarks',
  ]

  /* Distinct options for filter dropdowns */
  const periods = Array.from(new Set(allocations.map(a => a.period))).sort().reverse()
  const genOptions = masterGens.map(g => ({ label: `${g.name} (${g.code || g.id})`, value: g.id }))
  const powerTypeOptions = powerTypes
    .filter(pt => pt.status === 'active')
    .map(pt => ({ label: pt.name, value: pt.name }))

  /* Filtered list */
  const filtered = allocations.filter(a => {
    const matchSearch = a.id.toLowerCase().includes(search.toLowerCase()) ||
      a.generatorName.toLowerCase().includes(search.toLowerCase()) ||
      a.period.toLowerCase().includes(search.toLowerCase())
    const matchStatus = pendingOnly
      ? [AllocationStatus.SUBMITTED, AllocationStatus.UNDER_REVIEW].includes(a.status)
      : (!filterStatus || a.status === filterStatus)
    const matchGen       = !filterGen       || a.generatorId === filterGen
    const matchPowerType = !filterPowerType || (() => {
      const gen = masterGens.find(g => g.id === a.generatorId)
      return gen?.typeOfPower?.includes(filterPowerType) || gen?.plantType?.includes(filterPowerType)
    })()
    const matchPeriod    = !filterPeriod    || a.period === filterPeriod
    return matchSearch && matchStatus && matchGen && matchPowerType && matchPeriod
  })

  /* KPI */
  const kpi = {
    total:    allocations.length,
    pending:  allocations.filter(a => [AllocationStatus.SUBMITTED, AllocationStatus.UNDER_REVIEW].includes(a.status)).length,
    approved: allocations.filter(a => a.status === AllocationStatus.APPROVED).length,
    locked:   allocations.filter(a => a.status === AllocationStatus.LOCKED).length,
    units:    allocations
      .filter(a => [AllocationStatus.APPROVED, AllocationStatus.LOCKED].includes(a.status))
      .reduce((s, a) => s + (Number(a.totalAllocated) || 0), 0),
  }

  const kpiCards = [
    {
      label: 'Total Allocations',
      value: kpi.total,
      icon: <PieChartOutlined style={{ fontSize: 24, color: '#001A4D' }} />,
      iconBg: 'rgba(0,26,77,0.08)',
      borderColor: '#001A4D',
    },
    {
      label: 'Pending Approval',
      value: kpi.pending,
      icon: <ClockCircleOutlined style={{ fontSize: 24, color: '#F59E0B' }} />,
      iconBg: 'rgba(245,158,11,0.08)',
      borderColor: '#F59E0B',
    },
    {
      label: 'Approved',
      value: kpi.approved + kpi.locked,
      icon: <CheckSquareOutlined style={{ fontSize: 24, color: '#00A651' }} />,
      iconBg: 'rgba(0,166,81,0.08)',
      borderColor: '#00A651',
    },
    {
      label: 'Total Allocated (Approved)',
      value: (
        <>
          {(kpi.units / 1000).toFixed(1)}
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginLeft: 4 }}>MWh</span>
        </>
      ),
      icon: <ThunderboltOutlined style={{ fontSize: 24, color: '#2563EB' }} />,
      iconBg: 'rgba(37,99,235,0.08)',
      borderColor: '#2563EB',
    },
  ]

  // Every invoice created here should get its number from Code Master, same
  // as everywhere else in the app — falls back to the old hand-built format
  // only if that call itself fails (offline/server error), so an approval
  // never gets stuck on invoice numbering.
  const nextInvoiceNumber = async (fallback: string): Promise<string> => {
    try {
      return await generateCode('invoices')
    } catch (err) {
      console.error('[allocation] generateCode(invoices) failed, using fallback format:', err)
      if (err instanceof ApiError && err.message) message.warning(`Couldn't reach Code Master for an invoice number (${err.message}) — used a fallback format instead.`)
      return fallback
    }
  }

  // Re-derive tariffs from the live Tariff Master instead of trusting the
  // allocation's frozen snapshot (captured back when it was drafted/submitted
  // in AllocationForm). A generator/consumer very often gets its tariff set
  // up in Tariff Master AFTER the allocation was submitted — with only the
  // stale snapshot consulted, that permanently stuck the generator invoice at
  // "skipped" and consumer invoices at ₹0/missing, even after the tariff was
  // fixed, because nothing ever re-checked Tariff Master. Falls back to the
  // snapshot only if no live tariff exists. Mutates + persists the allocation
  // so it stops misreporting the tariff it was actually billed at.
  const reconcileTariffs = (allocation: MockAllocation) => {
    const generatorTariff = resolveGeneratorTariff(tariffs, allocation.generatorId) ?? allocation.generatorTariff
    const consumerAllocations = (allocation.consumerAllocations || []).map(ca => {
      const tariff = ca.tariff || resolveConsumerTariff(tariffs, ca.id, allocation.generatorId) || 0
      return { ...ca, tariff }
    })
    allocation.generatorTariff = generatorTariff
    allocation.consumerAllocations = consumerAllocations
    allocationStore.save({ id: allocation.id, generatorTariff, consumerAllocations })
    setAllocations(prev => prev.map(a => a.id === allocation.id ? { ...a, generatorTariff, consumerAllocations } : a))
    return allocation
  }

  // Shared by both the Approve action and the standalone "Generate Invoices"
  // retry (for allocations already Approved/Locked whose tariff was missing
  // at approval time and has since been fixed in Tariff Master).
  const generateInvoicesForAllocation = async (allocation: MockAllocation, approvedAt: string) => {
    // Idempotency guards — this allocation can come back through here more
    // than once (e.g. edited and resubmitted after an earlier approval, per
    // UA-04, or retried via "Generate Invoices"). Checked per invoice TYPE,
    // not as a single all-or-nothing flag: a prior approval can have
    // generated the consumer invoices but skipped the generator payable one
    // (e.g. no generator tariff was set yet in Tariff Master at that time) —
    // retrying after fixing that should retry only the missing generator
    // invoice, not refuse outright because the consumer invoices already exist.
    const existingForAllocation = invoiceStore.getAll()
      .filter(inv => inv.allocationId === allocation.id && inv.status !== 'cancelled')
    const hasConsumerInvoices = existingForAllocation.some(inv => inv.type === 'consumer')
    const hasGeneratorInvoice = existingForAllocation.some(inv => inv.type === 'generator')
    if (hasConsumerInvoices && hasGeneratorInvoice) {
      message.warning('Invoices already exist for this allocation — no new invoices were generated. Cancel the existing ones first if they need to be replaced.')
      return
    }

    // GST rates sourced from GST Master (electricity slab → fallback to first active → 2.5%)
    const elecGstExact = gstConfigs.find(g => g.status === 'active' && /electric/i.test(`${g.slabName} ${g.applicableFor}`))
    const elecGst = elecGstExact || gstConfigs.find(g => g.status === 'active')
    const cgstRate = (elecGst ? elecGst.cgstRate : 2.5) / 100
    const sgstRate = (elecGst ? elecGst.sgstRate : 2.5) / 100
    const gstTotalRate = cgstRate + sgstRate
    if (!elecGstExact) {
      message.warning(elecGst
        ? `No GST slab tagged "Electricity" found — used "${elecGst.slabName}" (${Math.round(gstTotalRate * 100)}%) instead. Check GST Master.`
        : 'No active GST slab found — invoices generated with a fallback 5% (2.5% CGST + 2.5% SGST) rate. Set up GST Master.')
    }

    // Auto-generate draft invoices
    let generatedCount = 0
    let consumerInvCount = 0
    const skippedConsumers: string[] = []
    if (hasConsumerInvoices) {
      // already generated in a previous approval — only the generator side
      // (checked below) might still be missing.
    } else if (allocation.consumerAllocations) {
      for (const [idx, ca] of allocation.consumerAllocations.entries()) {
        if (!ca.tariff || ca.tariff <= 0) { skippedConsumers.push(ca.name); continue }

        const masterConsumer = masterCons.find(c => c.id === ca.id)
        const units = ca.allocatedUnits
        const tariff = ca.tariff
        const baseAmount = units * tariff

        const intra = isIntraState(masterConsumer?.state)
        const cgstAmount = intra ? baseAmount * cgstRate : 0
        const sgstAmount = intra ? baseAmount * sgstRate : 0
        const igstAmount = intra ? 0 : baseAmount * gstTotalRate
        const gstAmount = cgstAmount + sgstAmount + igstAmount
        const totalAmount = baseAmount + gstAmount

        const invoiceData: Partial<Invoice> = {
          invoiceNumber: await nextInvoiceNumber(`SP/C/${allocation.period.replace(' ', '')}/${String(idx + 1).padStart(3, '0')}-${invoiceSuffix()}`),
          type: 'consumer',
          entity: ca.name,
          entityId: ca.id,
          allocationId: allocation.id,
          period: allocation.period,
          units,
          tariff,
          sac: '998312',
          placeOfSupply: formatPlaceOfSupply(masterConsumer?.state),
          baseAmount,
          cgstAmount,
          sgstAmount,
          igstAmount,
          gstAmount,
          totalAmount,
          paidAmount: 0,
          status: 'draft',
          dueDate: new Date(Date.now() + 15 * 86400000).toISOString().slice(0, 10), // +15 days
          irn: null,
          ewayBill: null,
          gstr1Filed: false,
          gstr3bFiled: false,
          issueDate: approvedAt,
          clientGstNo: masterConsumer?.gstin || '',
          clientAddress: masterConsumer?.companyAddress ? [masterConsumer.companyAddress] : [],
          customerRef: masterConsumer?.htscNumber || '',
          notes: `${allocation.period} auto-generated draft invoice based on approved allocation ${allocation.id}.`,
        }
        invoiceStore.save(invoiceData)
        generatedCount++
        consumerInvCount++
      }
    }

    // Generator-side payable invoice (Shakthi → generator) at the generator tariff
    let genInvoiceCreated = false
    let genInvoiceSkipped = false
    if (hasGeneratorInvoice) {
      // already generated in a previous approval — nothing to do.
    } else if (allocation.generatorTariff && allocation.totalAllocated) {
      const masterGen = masterGens.find(g => g.id === allocation.generatorId)
      const gUnits  = allocation.totalAllocated
      const gTariff = allocation.generatorTariff
      const gBase   = gUnits * gTariff
      const gIntra  = isIntraState(masterGen?.state)
      const gCgst   = gIntra ? gBase * cgstRate : 0
      const gSgst   = gIntra ? gBase * sgstRate : 0
      const gIgst   = gIntra ? 0 : gBase * gstTotalRate
      const gGst    = gCgst + gSgst + gIgst

      const genInvoice: Partial<Invoice> = {
        invoiceNumber: await nextInvoiceNumber(`SP/G/${allocation.period.replace(' ', '')}/${allocation.generatorId}-${invoiceSuffix()}`),
        type: 'generator',
        entity: allocation.generatorName,
        entityId: allocation.generatorId,
        allocationId: allocation.id,
        period: allocation.period,
        units: gUnits,
        tariff: gTariff,
        sac: '998312',
        placeOfSupply: formatPlaceOfSupply(masterGen?.state),
        baseAmount: gBase,
        cgstAmount: gCgst,
        sgstAmount: gSgst,
        igstAmount: gIgst,
        gstAmount: gGst,
        totalAmount: gBase + gGst,
        paidAmount: 0,
        status: 'draft',
        dueDate: new Date(Date.now() + 15 * 86400000).toISOString().slice(0, 10),
        irn: null,
        ewayBill: null,
        gstr1Filed: false,
        gstr3bFiled: false,
        issueDate: approvedAt,
        clientGstNo: masterGen?.gstin || '',
        clientAddress: masterGen?.companyAddress ? [masterGen.companyAddress] : [],
        customerRef: masterGen?.htServiceNumber || '',
        notes: `${allocation.period} auto-generated generator payable for ${gUnits.toLocaleString('en-IN')} units @ ₹${gTariff}/unit (allocation ${allocation.id}).`,
      }
      invoiceStore.save(genInvoice)
      generatedCount++
      genInvoiceCreated = true
    } else {
      genInvoiceSkipped = true
    }

    if (generatedCount > 0) {
      const parts: string[] = []
      if (consumerInvCount > 0) parts.push(`${consumerInvCount} consumer`)
      if (genInvoiceCreated) parts.push('1 generator payable')
      message.success(`${generatedCount} draft invoice(s) generated — ${parts.join(' + ')}.`)
    } else if (hasConsumerInvoices || hasGeneratorInvoice) {
      // everything that could be generated already had been (e.g. the only
      // thing left missing is still blocked on a tariff — the warning below
      // explains why) — say so plainly instead of a hollow "0 generated".
      message.info('No new invoices were needed — everything already existed.')
    }
    if (skippedConsumers.length) {
      message.warning(`No tariff set for ${skippedConsumers.join(', ')} — no invoice created for ${skippedConsumers.length === 1 ? 'them' : 'those consumers'}. Set a tariff in Tariff Master, then use "Generate Invoices" to retry.`)
    }
    if (genInvoiceSkipped) {
      message.warning('No generator tariff set for this allocation — the generator payable invoice was NOT created. Set a tariff in Tariff Master, then use "Generate Invoices" to retry.')
    }
  }

  // Defense-in-depth: AllocationForm now blocks over-allocation before a
  // record can ever reach Submitted, but nothing here re-checked that before
  // flipping status to Approved/Locked — so a bad record from any other
  // write path (a pre-existing bad row, a script, a future form change)
  // would sail through unblocked and straight into invoice generation.
  // Recomputed from the stored totals, not a form-time flag.
  //
  // Must apply the same T&D loss % the Form checks against (tdLossPct,
  // defaulting to 3 for legacy rows saved before that column existed — same
  // default AllocationForm uses) — comparing against raw totalGenerated
  // instead let a record the Form would have rejected sail through here.
  const overAllocationError = (allocation: MockAllocation): string | null => {
    const totalGenerated = Number(allocation.totalGenerated) || 0
    const totalAllocated = Number(allocation.totalAllocated) || 0
    const tdLossPct = allocation.tdLossPct ?? 3
    const availableAfterTd = Math.round(totalGenerated * (1 - tdLossPct / 100))
    if (totalAllocated > availableAfterTd + 1) {
      return `Total allocated (${totalAllocated.toLocaleString('en-IN')} kWh) exceeds what's available after ${tdLossPct}% T&D loss (${availableAfterTd.toLocaleString('en-IN')} kWh) — fix the allocation before approving/locking it.`
    }
    return null
  }

  const handleApprove = async (id: string) => {
    const allocation = allocations.find(a => a.id === id)
    if (!allocation) return
    const overAllocErr = overAllocationError(allocation)
    if (overAllocErr) { message.error(overAllocErr); return }
    reconcileTariffs(allocation)

    const approvedAt = new Date().toISOString().slice(0, 10)
    allocationStore.save({ id, status: AllocationStatus.APPROVED, approvedBy: 'Dept Head Kumar', approvedAt })
    setAllocations(prev => prev.map(a => a.id === id
      ? { ...a, status: AllocationStatus.APPROVED, approvedBy: 'Dept Head Kumar', approvedAt }
      : a
    ))

    await generateInvoicesForAllocation(allocation, approvedAt)
  }

  // Retry for allocations already Approved/Locked whose generator and/or
  // consumer tariffs weren't set up in Tariff Master yet at approval time —
  // there's no "Approve" button left to click on these (only shown for
  // Submitted/Under Review), so without this they'd be stuck missing
  // invoices forever even after the tariff is fixed.
  const handleGenerateInvoices = async (id: string) => {
    const allocation = allocations.find(a => a.id === id)
    if (!allocation) return
    reconcileTariffs(allocation)
    await generateInvoicesForAllocation(allocation, allocation.approvedAt || new Date().toISOString().slice(0, 10))
  }

  const handleReject = () => {
    if (!selectedReason) { message.warning('Please select a rejection reason'); return }
    allocationStore.save({ id: rejectModal.id, status: AllocationStatus.REJECTED, rejectionReason: selectedReason })
    setAllocations(prev => prev.map(a => a.id === rejectModal.id
      ? { ...a, status: AllocationStatus.REJECTED, rejectionReason: selectedReason }
      : a
    ))
    message.error('Allocation rejected — submitter has been notified.')
    setRejectModal({ open: false, id: '' })
    setSelectedReason('')
  }

  const handleLock = (id: string) => {
    const allocation = allocations.find(a => a.id === id)
    if (!allocation) return
    const overAllocErr = overAllocationError(allocation)
    if (overAllocErr) { message.error(overAllocErr); return }
    allocationStore.save({ id, status: AllocationStatus.LOCKED })
    setAllocations(prev => prev.map(a => a.id === id ? { ...a, status: AllocationStatus.LOCKED } : a))
    message.success('Allocation locked. No further changes allowed without re-approval.')
  }

  const columns = [
    {
      title: 'Allocation ID',
      dataIndex: 'id', key: 'id', width: 110,
      render: (text: string, r: MockAllocation) => (
        <span 
          style={{ fontWeight: 800, color: '#1677ff', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, cursor: 'pointer' }}
          onClick={() => navigate(`/allocations/${r.id}/edit`)}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Generator',
      key: 'gen', width: 200,
      render: (_: any, r: MockAllocation) => (
        <div className="ua-gen-chip">
          <div className="ua-gen-avatar">⚡</div>
          <div>
            <div className="ua-gen-name">{r.generatorName}{r.plantName ? ` — ${r.plantName}` : ''}</div>
            <div className="ua-gen-sub">{r.generatorId} · {r.period}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Generated',
      key: 'generated', width: 120,
      render: (_: any, r: MockAllocation) => (
        <div>
          <div className="ua-units">{r.totalGenerated.toLocaleString('en-IN')}</div>
          <div className="ua-units-label">kWh</div>
        </div>
      ),
    },
    {
      title: 'Allocated',
      key: 'allocated', width: 160,
      render: (_: any, r: MockAllocation) => {
        const pct = r.totalGenerated > 0 ? Math.round((r.totalAllocated / r.totalGenerated) * 100) : 0
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="ua-units" style={{ fontSize: 13 }}>{r.totalAllocated.toLocaleString('en-IN')}</span>
              <span style={{ fontSize: 11.5, color: '#00A651', fontWeight: 700 }}>{pct}%</span>
            </div>
            <div className="ua-allocation-bar">
              <div className="ua-allocation-bar-fill" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )
      },
    },
    {
      title: 'T&D Loss',
      key: 'tdloss', width: 100,
      render: (_: any, r: MockAllocation) => (
        <div>
          <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, fontWeight: 700, color: r.tdLoss > 0 ? '#DC2626' : '#8A93A6' }}>
            {r.tdLoss.toLocaleString('en-IN')}
          </div>
          <div style={{ fontSize: 11, color: '#8A93A6' }}>kWh</div>
        </div>
      ),
    },
    {
      title: 'Consumers',
      key: 'consumers', width: 90,
      render: (_: any, r: MockAllocation) => (
        <Badge count={r.consumerCount} showZero
          style={{ backgroundColor: '#EFF4FF', color: '#1E3A8A', fontWeight: 800, boxShadow: 'none', border: '1px solid #BFDBFE' }} />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status', key: 'status', width: 140,
      render: (status: AllocationStatus) => {
        const cfg = STATUS_CFG[status]
        return <Tag icon={cfg.icon} color={cfg.color} style={{ borderRadius: 6, fontWeight: 700, fontSize: 12 }}>{cfg.label}</Tag>
      },
    },
    {
      title: 'Submitted',
      key: 'submittedBy', width: 130,
      render: (_: any, r: MockAllocation) => (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0B1426' }}>{r.submittedBy}</div>
          <div style={{ fontSize: 11.5, color: '#8A93A6' }}>
            {formatDate(r.submittedAt, { day: '2-digit', month: 'short' })}
          </div>
        </div>
      ),
    },
    {
      title: 'Actions', key: 'actions', width: 170, fixed: 'right' as const,
      render: (_: any, r: MockAllocation) => (
        <Space size={4}>
          <Tooltip title="View / Edit">
            <Button className="ua-icon-btn" icon={<EyeOutlined />}
              onClick={() => navigate(`/allocations/${r.id}/edit`)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button className="ua-icon-btn" icon={<EditOutlined />}
              onClick={() => navigate(`/allocations/${r.id}/edit`)}
              disabled={r.status === AllocationStatus.LOCKED || r.status === AllocationStatus.APPROVED} />
          </Tooltip>
          {[AllocationStatus.SUBMITTED, AllocationStatus.UNDER_REVIEW].includes(r.status) && (
            <>
              <Tooltip title="Approve">
                <Popconfirm
                  title="Approve Allocation?"
                  description="Invoices can be generated once approved."
                  onConfirm={() => handleApprove(r.id)}
                  okText="Approve" cancelText="Cancel"
                  okButtonProps={{ style: { background: '#00A651', borderColor: '#00A651' } }}
                >
                  <Button className="ua-icon-btn ua-icon-btn-approve" icon={<CheckCircleOutlined />} />
                </Popconfirm>
              </Tooltip>
              <Tooltip title="Reject">
                <Button className="ua-icon-btn ua-icon-btn-reject" icon={<CloseCircleOutlined />}
                  onClick={() => setRejectModal({ open: true, id: r.id })} />
              </Tooltip>
            </>
          )}
          {r.status === AllocationStatus.APPROVED && (
            <Tooltip title="Lock Allocation">
              <Popconfirm
                title="Lock this allocation?"
                description="Once locked, changes require full re-approval."
                onConfirm={() => handleLock(r.id)}
                okText="Lock" cancelText="Cancel"
                okButtonProps={{ style: { background: '#F59E0B', borderColor: '#F59E0B' } }}
              >
                <Button className="ua-icon-btn ua-icon-btn-lock" icon={<LockOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
          {[AllocationStatus.APPROVED, AllocationStatus.LOCKED].includes(r.status) && (() => {
            const existing = invoiceStore.getAll().filter(inv => inv.allocationId === r.id && inv.status !== 'cancelled')
            const missing = !existing.some(inv => inv.type === 'consumer') || !existing.some(inv => inv.type === 'generator')
            if (!missing) return null
            return (
              <Tooltip title="Generate any consumer/generator invoices still missing for this allocation (e.g. a tariff was only just set up in Tariff Master).">
                <Popconfirm
                  title="Generate missing invoices?"
                  description="Re-checks Tariff Master and creates any consumer/generator invoice that's still missing."
                  onConfirm={() => handleGenerateInvoices(r.id)}
                  okText="Generate" cancelText="Cancel"
                >
                  <Button className="ua-icon-btn" icon={<ThunderboltOutlined />} />
                </Popconfirm>
              </Tooltip>
            )
          })()}
        </Space>
      ),
    },
  ]

  if (!allocationStore.loaded) return <TableSkeleton columns={9} />

  return (
    <div className="ua-page">

      {/* ── KPI Strip ──────────────────────────────────────── */}
      <KpiCardRow cards={kpiCards} />

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="ua-header">
        <div className="ua-header-left">
          <h2>⊞ Unit Allocation</h2>
          <p>Distribute approved generation units to consumers based on share-holding mapping</p>
        </div>
        <div className="ua-header-actions">
          <Button icon={<ReloadOutlined />} onClick={() => { setAllocations(allocationStore.getAll()); message.info('Refreshed') }} />
          <Button type="primary" icon={<PlusOutlined />} size="large"
            onClick={() => navigate('/allocations/new')} style={{ fontWeight: 700 }}>
            New Allocation
          </Button>
        </div>
      </div>

      {/* ── Filters ────────────────────────────────────────── */}
      <Card style={{ marginBottom: 14, borderRadius: 12, border: '1px solid #E4E8F0' }}>
        <div style={{ padding: '0' }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={6}>
              <Input placeholder="Search by ID, Generator, Period..."
                prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
                value={search} onChange={e => setSearch(e.target.value)} allowClear />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select placeholder="Generator" allowClear value={filterGen || undefined}
                onChange={setFilterGen} style={{ width: '100%' }}
                showSearch optionFilterProp="label"
                options={genOptions} />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select placeholder="Type of Power" allowClear value={filterPowerType || undefined}
                onChange={setFilterPowerType} style={{ width: '100%' }}
                showSearch optionFilterProp="label"
                options={powerTypeOptions} />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select placeholder="Period" allowClear value={filterPeriod || undefined}
                onChange={setFilterPeriod} style={{ width: '100%' }}
                options={periods.map(p => ({ label: p, value: p }))} />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select placeholder="Status" allowClear disabled={pendingOnly} value={filterStatus || undefined}
                onChange={(v) => { setStatus(v); setPendingOnly(false) }} style={{ width: '100%' }}
                options={Object.values(AllocationStatus).map(s => ({ label: s, value: s }))} />
            </Col>
            <Col xs={24} sm={12} md={2}>
              <Button block onClick={() => { setSearch(''); setStatus(''); setFilterGen(''); setFilterPowerType(''); setFilterPeriod(''); setPendingOnly(false) }}>Clear</Button>
            </Col>
          </Row>
        </div>
      </Card>

      {/* Results meta */}
      <div style={{ fontSize: 12, color: '#8A93A6', marginBottom: 8, textAlign: 'right' }}>
        Showing <strong>{filtered.length}</strong> of <strong>{allocations.length}</strong> allocations
        {kpi.pending > 0 && (
          <Tooltip title={pendingOnly ? 'Showing only allocations awaiting approval — click to show all' : 'Click to show only allocations awaiting approval'}>
            <span
              onClick={() => setPendingOnly(p => !p)}
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', marginLeft: 12 }}
            >
              <Badge count={kpi.pending} style={{ backgroundColor: '#F59E0B' }} />
              <span
                style={{
                  marginLeft: 6, color: '#D97706', fontWeight: 700,
                  textDecoration: pendingOnly ? 'underline' : undefined,
                }}
              >
                ⚠️ awaiting approval
              </span>
            </span>
          </Tooltip>
        )}
      </div>

      {/* ── Table ──────────────────────────────────────────── */}
      <Card className="ua-table-card">
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}` }}
          expandable={{
            expandedRowRender: (r: MockAllocation) => (
              <div style={{ padding: '12px 16px', background: '#F8FAFC', borderRadius: 8 }}>
                <Row gutter={[12, 8]}>
                  <Col xs={24} md={12}>
                    <div style={{ background: '#fff', border: '1px solid #E4E8F0', borderRadius: 8, padding: '12px 14px' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#8A93A6', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
                        Allocation Summary
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, color: '#4A5568' }}>Total Generated</span>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800 }}>{r.totalGenerated.toLocaleString('en-IN')} kWh</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, color: '#4A5568' }}>Total Allocated</span>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, color: '#00A651' }}>{r.totalAllocated.toLocaleString('en-IN')} kWh</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color: '#4A5568' }}>T&D Loss</span>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, color: '#DC2626' }}>{r.tdLoss.toLocaleString('en-IN')} kWh</span>
                      </div>
                      <Progress percent={r.totalGenerated > 0 ? Math.round((r.totalAllocated / r.totalGenerated) * 100) : 0}
                        strokeColor="#00A651" trailColor="#E4E8F0" size="small" />
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    {r.rejectionReason && (
                      <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 14px', color: '#DC2626', fontSize: 13 }}>
                        <strong>Rejection Reason:</strong> {r.rejectionReason}
                      </div>
                    )}
                    {r.approvedBy && (
                      <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '12px 14px', color: '#00A651', fontSize: 13 }}>
                        <strong>Approved by:</strong> {r.approvedBy} on{' '}
                        {formatDate(r.approvedAt, { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    )}
                    {r.status === AllocationStatus.SUBMITTED && (
                      <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '12px 14px', color: '#92400E', fontSize: 13 }}>
                        ⏳ Pending department head review before invoices can be generated.
                      </div>
                    )}
                  </Col>
                </Row>
              </div>
            ),
            rowExpandable: () => true,
          }}
        />
      </Card>

      {/* ── Reject Modal with predefined reasons ───────────── */}
      <Modal
        title={<span style={{ color: '#DC2626' }}><CloseCircleOutlined style={{ marginRight: 8 }} />Reject Allocation</span>}
        open={rejectModal.open}
        onCancel={() => { setRejectModal({ open: false, id: '' }); setSelectedReason('') }}
        footer={null} width={520}
      >
        <p style={{ color: '#4A5568', marginBottom: 16, fontSize: 13 }}>
          Select a reason from the predefined list. The submitter will be notified and may re-submit after corrections.
        </p>
        <div>
          {REJECTION_REASONS.map(r => (
            <div key={r}
              className={`ua-rejection-reason-item ${selectedReason === r ? 'selected' : ''}`}
              onClick={() => setSelectedReason(r)}
            >
              {selectedReason === r ? '✓ ' : ''}{r}
            </div>
          ))}
        </div>
        {selectedReason && (
          <div style={{ marginTop: 14, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#DC2626' }}>
            <strong>Selected:</strong> {selectedReason}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <Button onClick={() => { setRejectModal({ open: false, id: '' }); setSelectedReason('') }}>Cancel</Button>
          <Button type="primary" danger onClick={handleReject} disabled={!selectedReason}>
            Reject Allocation
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default AllocationList
